import type { OverlayTrack } from '../components/central-monitoring/CentralMonitoringOverlays';
import {
  maxServerMissForDisplay,
  passesDetectionDisplayFilters,
  type MonitoringAiSettings,
} from '../constants/monitoringAiSettings';
import {
  parseOpenPoseOverlay,
  type OpenPoseOverlayPayload,
} from '../constants/openposeOverlay';

export type ServerSnapshotPayload = {
  detections: unknown[];
  risk_alerts: unknown[];
  mount_type?: string;
  frame_width?: number;
  frame_height?: number;
  updated_at?: number;
  openpose_overlay?: OpenPoseOverlayPayload | null;
  openpose_ran?: boolean;
  /** raw_person = 원본 YOLO 진단 모드 */
  pipeline_mode?: string;
};

export function serverSnapshotKey(poolId: number, cctvId: number): string {
  return `${Number(poolId)}:${Number(cctvId)}`;
}

export function isRawPersonPipelineMode(mode: unknown): boolean {
  const m = String(mode || '')
    .trim()
    .toLowerCase();
  return m === 'raw_person' || m === 'raw_person_debug';
}

export function lookupServerSnapshot(
  map: Record<string, ServerSnapshotPayload>,
  poolId: number,
  cctvId: number
): ServerSnapshotPayload | undefined {
  if (!Number.isFinite(poolId) || !Number.isFinite(cctvId)) return undefined;
  return map[serverSnapshotKey(poolId, cctvId)];
}

/**
 * 서버 central-ai 스냅샷 — 백엔드에서 이미 FP·트래킹 export 된 bbox.
 * 프론트 하이브리드 FP를 다시 적용하면 미검출(0박스)이 자주 발생한다.
 * rawPerson 이면 conf/면적 필터도 거의 생략(원본 YOLO 진단).
 */
export function filterServerDetectionsForDisplay(
  detections: unknown[],
  settings: MonitoringAiSettings,
  visibleClassMap: Record<string, boolean>,
  frameW: number,
  frameH: number,
  options?: { rawPerson?: boolean }
): Array<Record<string, unknown>> {
  const rawPerson = Boolean(options?.rawPerson);
  const maxMiss = maxServerMissForDisplay(settings);
  const fw = Math.max(1, frameW);
  const fh = Math.max(1, frameH);
  const displaySettings: MonitoringAiSettings = {
    ...settings,
    min_track_hits: 1,
    min_confidence: rawPerson
      ? Math.min(0.12, Number(settings.min_confidence) || 0.12)
      : settings.min_confidence,
    min_box_side_px: rawPerson ? 4 : settings.min_box_side_px,
    min_box_area_ratio: rawPerson ? 0.00005 : settings.min_box_area_ratio,
  };
  let fallbackId = 1;
  const out: Array<Record<string, unknown>> = [];
  for (const raw of detections || []) {
    if (!raw || typeof raw !== 'object') continue;
    const det = raw as Record<string, unknown>;
    const bbox = det.bbox;
    if (!Array.isArray(bbox) || bbox.length !== 4) continue;
    if (!rawPerson && Number(det.miss_count ?? 0) > maxMiss) continue;
    const className = String(det.class_name || 'person').toLowerCase();
    if (visibleClassMap[className] === false) continue;
    const score = Number(det.score ?? det.confidence ?? 0);
    const hits = Math.max(1, Number(det.hits ?? 1));
    if (rawPerson) {
      if (score < displaySettings.min_confidence) continue;
    } else if (
      !passesDetectionDisplayFilters(
        bbox.map(Number),
        score,
        fw,
        fh,
        displaySettings,
        hits
      )
    ) {
      continue;
    }
    let tid = Number(det.track_id ?? det.id);
    if (!Number.isFinite(tid)) {
      tid = 1_000_000 + fallbackId;
      fallbackId += 1;
    }
    out.push({
      ...det,
      bbox: bbox.map(Number),
      track_id: tid,
      id: tid,
      class_name: className,
      score,
      confidence: score,
      hits,
      frame_width: Number(det.frame_width) || fw,
      frame_height: Number(det.frame_height) || fh,
      miss_count: Number(det.miss_count ?? 0),
    });
  }
  return out;
}

/** 통합 관제와 동일: central-ai 스냅샷 → 오버레이 트랙 (웨어러블 매칭은 팝업에서 생략) */
export function overlayTracksFromServerSnapshot(
  serverSnap: ServerSnapshotPayload,
  wearableTrackMap: Record<string, { wearable_id?: string; confidence?: number }> = {}
): OverlayTrack[] {
  const fw = Math.max(1, Number(serverSnap.frame_width) || 1280);
  const fh = Math.max(1, Number(serverSnap.frame_height) || 720);
  const dets = Array.isArray(serverSnap.detections) ? serverSnap.detections : [];
  return dets
    .filter(
      (det: unknown) =>
        det &&
        typeof det === 'object' &&
        Array.isArray((det as { bbox?: unknown }).bbox) &&
        (det as { bbox: unknown[] }).bbox.length === 4
    )
    .map((det: any, i: number) => {
      const tid = Number(det.track_id ?? det.id);
      const trackId = Number.isFinite(tid) && tid > 0 ? tid : i + 1;
      const match = wearableTrackMap[String(trackId)];
      const rawBbox = (det.bbox as number[]).map(Number);
      const maxCoord = rawBbox.length
        ? Math.max(...rawBbox.map((v) => Math.abs(Number(v) || 0)))
        : 0;
      const displayBbox =
        maxCoord > 0 && maxCoord <= 1.5
          ? [rawBbox[0] * fw, rawBbox[1] * fh, rawBbox[2] * fw, rawBbox[3] * fh]
          : rawBbox;
      return {
        trackId,
        displayBbox,
        class_name: String(det.class_name || 'person'),
        score: Number(det.score ?? det.confidence ?? 0),
        frame_width: fw,
        frame_height: fh,
        alpha: 1,
        wearableId: match?.wearable_id,
        wearableConfidence: match?.confidence,
      } as OverlayTrack;
    });
}

/** OpenPose person_id ↔ track_id 매칭 시 bbox 라벨에 자세 힌트 */
export function enrichOverlayTracksWithOpenPose(
  tracks: OverlayTrack[],
  overlay: OpenPoseOverlayPayload | null | undefined
): OverlayTrack[] {
  if (!overlay?.persons?.length || !tracks.length) return tracks;
  const byId = new Map<number, { unusual: boolean; reason?: string }>();
  overlay.persons.forEach((p) => {
    const id = Number(p.id);
    if (!Number.isFinite(id) || id < 0) return;
    byId.set(id, {
      unusual: Boolean(p.unusual),
      reason: p.reason,
    });
  });
  if (byId.size === 0) return tracks;
  return tracks.map((t) => {
    const meta = byId.get(Number(t.trackId));
    if (!meta) return t;
    const label =
      meta.unusual && meta.reason
        ? meta.reason
        : meta.unusual
          ? 'OpenPose 이상'
          : undefined;
    if (!label) return t;
    return { ...t, poseLabel: label };
  });
}

export function snapshotsToMap(
  rows: Array<{
    pool_id: number;
    cctv_id: number;
    detections?: unknown[];
    risk_alerts?: unknown[];
    mount_type?: string;
    frame_width?: number;
    frame_height?: number;
    updated_at?: number;
    openpose_overlay?: unknown;
    openpose_ran?: boolean;
  }>
): Record<string, ServerSnapshotPayload> {
  const map: Record<string, ServerSnapshotPayload> = {};
  rows.forEach((s) => {
    const pid = Number(s.pool_id);
    const cctvId = Number(s.cctv_id);
    if (!Number.isFinite(pid) || !Number.isFinite(cctvId)) return;
    map[serverSnapshotKey(pid, cctvId)] = {
      detections: s.detections || [],
      risk_alerts: s.risk_alerts || [],
      mount_type: s.mount_type,
      frame_width: s.frame_width,
      frame_height: s.frame_height,
      updated_at: s.updated_at,
      openpose_overlay: parseOpenPoseOverlay(s.openpose_overlay),
      openpose_ran: Boolean(s.openpose_ran),
    };
  });
  return map;
}
